import React, { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import BootScreen from './components/boot-screen/BootScreen';
import CrashProbe from './components/error-boundary/CrashProbe';
import RouteErrorBoundary from './components/error-boundary/RouteErrorBoundary';
import ProtectedRoute from './components/protected-route/ProtectedRoute';
import { PROJECT_TYPES, PROJECT_VARIANTS, USER_ROLES } from './constants';
import Layout from './layout/Layout';
import ProjectsList from './projects/projects-list/ProjectsList';
import { checkAuth } from './redux/security/securityActions';
import { ProjectProvider } from './context/ProjectContext';
import Customers from './customers/Customers';
import EditProject from './projects/edit-project/EditProject';
import ProjectCreate from './projects/project-create/ProjectCreate';
import SonarCollectData from './projects/project-create/sonar/collect-data/SonarCollectData';
import ScoutCollectData from './scout-collect-data/ScoutCollectData';
import CreateUser from './user-management/CreateUser/CreateUser';
import EditUser from './user-management/EditUser/EditUser';
import Users from './user-management/Users/Users';
import Settings from './settings/Settings';
import ProjectRequest from './project-request/ProjectRequest';
import AiPlayground from './ai-playground/AiPlayground';
import MediaStudioPage from './media-studio/MediaStudioPage';

import 'react-tooltip/dist/react-tooltip.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './font/inter/inter.css';
import './scss/index.scss';

const GenieGo = lazy(() => import('./genie-go/GenieGo'));
const Genie = lazy(() => import('./genie/Genie'));
const Id8 = lazy(() => import('./id8/Id8'));
const Resonance = lazy(() => import('./resonance/Resonance'));
const Scout = lazy(() => import('./scout/Scout'));
const SonarDashboard = lazy(() => import('./sonar/Dashboard'));
const Activ8 = lazy(() => import('./activ8/Activ8'));

function App() {
  const dispatch = useDispatch();

  const isLoadingUserStatus = useSelector((state) => state.securityNew.isLoadingUserStatus);

  useEffect(() => {
    dispatch(checkAuth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingUserStatus) {
    return <BootScreen />;
  }

  return (
    <Layout>
      <RouteErrorBoundary>
        <CrashProbe target='page' />
        <Suspense fallback={<BootScreen embedded />}>
          <Routes>
            <Route exact path='/projects' element={<ProjectsList />} />
            <Route
              path='/customers/*'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path='/projects/create/*'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN} redirectPath='/projects'>
                  <ProjectCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path='/projects/edit/*'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
                  <EditProject />
                </ProtectedRoute>
              }
            />
            <Route
              path='/users'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path='/users/edit/:id'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
                  <EditUser />
                </ProtectedRoute>
              }
            />
            <Route
              path='/users/create'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
                  <CreateUser />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path='/project/scout/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.SCOUTV2}>
                  <Scout />
                </ProjectProvider>
              }
            />
            <Route
              exact
              path='/project/sonar/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.SONAR}>
                  <SonarDashboard />
                </ProjectProvider>
              }
            />
            <Route
              exact
              path='/project/resonance/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.RESONANCE}>
                  <Resonance />
                </ProjectProvider>
              }
            />
            <Route
              exact
              path='/project/genie/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.GENIE}>
                  <Genie />
                </ProjectProvider>
              }
            />
            <Route
              exact
              path='/project/genie-go/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.GENIE_GO}>
                  <GenieGo />
                </ProjectProvider>
              }
            />
            <Route
              exact
              path='/project/id8/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.ID8}>
                  <Id8 />
                </ProjectProvider>
              }
            />
            <Route path='/project/sonar/collect-data/:id' exact element={<SonarCollectData />} />
            <Route exact path='/project/scout/collect-data/:id' element={<ScoutCollectData />} />
            <Route exact path='/playground' element={<AiPlayground />} />
            <Route exact path='/media-studio' element={<MediaStudioPage />} />
            <Route
              exact
              path='/project/activ8/:id'
              element={
                <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.ACTIV8}>
                  <Activ8 />
                </ProjectProvider>
              }
            />

            <Route
              path='/settings/*'
              exact
              element={
                <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route path='/project-request' exact element={<ProjectRequest />} />
            <Route path='*' element={<Navigate to={'/projects'} />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </Layout>
  );
}

export default App;
